import './SourcesPage.scss';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FilterItem } from '../../shared/components/filter-box/models/FilterBoxModel';
import Title from '../../shared/components/title/Title';
import { useFilters } from '../../shared/contexts/filters/FiltersContext';
import { expandSelectedChatTypesToMonitorTypes } from '../../shared/helpers/MonitorTypeHelpers';
import { useTranslation } from 'react-i18next';
import { PROJECTS } from '../../shared/routes/path';
import FilterPanel from '../../shared/components/filter-panel/FilterPanel';
import { useGetDomainsStats } from '../../shared/api/hooks/monitor/data/sources/UseGetDomainsStats';
import { useProjectDates } from '../../shared/hooks/filters/UseProjectDates';
import useFiltersInitializing from '../../shared/hooks/filters/UseFiltersInitializing';
import { useGetDomainsDailyStats } from '../../shared/api/hooks/monitor/data/sources/UseGetDomainsDailyStats';
import { useDomainsStatsComparison } from './hooks/UseDomainsStatsComparison';
import { useDomainsStatsShare } from './hooks/UseDomainsStatsShare';
import { useDomainsDailyStatsMatrix } from './hooks/UseDomainsDailyStatsMatrix';
import { useCitationTypesShareData } from './hooks/UseCitationTypesShareData';
import { extractHostname, useProjectDomain } from '../../shared/hooks/projects/preview/UseProjectDomain';
import DomainsDailyStatsChart from './components/domains-daily-stats-chart/DomainsDailyStatsChart';
import CitationTypesShareBarChart from './components/citation-types-share-bar-chart/CitationTypesShareBarChart';
import { useGetCategoriesStats } from '../../shared/api/hooks/monitor/data/sources/UseGetCategoriesStats';
import { useGetDomainsWithSourcesStats } from '../../shared/api/hooks/monitor/data/sources/UseGetDomainsWithSourcesStats';
import { DomainsWithSourcesStatsTable } from './components/domains-with-sources-stats-table/DomainsWithSourcesStatsTable';
import { useDomainsWithSourcesStatsComparison } from './hooks/UseDomainsWithSourcesStatsComparison';
import { useDomainsWithSourcesStatsShare } from './hooks/UseDomainsWithSourcesStatsShare';
import { useGetSourceStats } from '../../shared/api/hooks/monitor/data/sources/UseGetSourceStats';
import { useSourceStatsComparison } from './hooks/UseSourceStatsComparison';
import { useSourcesStatsShare } from './hooks/UseSourcesStatsShare';
import { SourcesStatsTable } from './components/sources-stats-table/SourcesStatsTable';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import useCanManage from '../../shared/hooks/account/UseCanManage';
import useProjectNavigate from '../../shared/hooks/projects/preview/UseProjectNavigate';
import { useGetMonitorsForProject } from '../../shared/api/hooks/monitor/useGetMonitorsForProject';
import PromptGroupDropdown from '../../shared/components/prompt-group-dropdown/PromptGroupDropdown';
import usePromptGroupId, { ALL_PROMPT_GROUPS_ID } from '../../shared/hooks/filters/UsePromptGroupId';
import { updateDomainClassification } from '../../shared/services/projects/ProjectService';
import ComparisonDateRangePicker from './components/comparison-date-range-picker/ComparisonDateRangePicker';
import { differenceInDays, format, subDays } from 'date-fns';
import { useGetCommonKeywords } from '../../shared/api/hooks/monitor/keyword/useGetCommonKeywords';
import { KeywordResponse } from '../../shared/models/monitor/KeywordResponse';
import { useGetTags } from '../../shared/api/hooks/tags/useGetTags';
import { withTagBrandNames } from './helpers/SourcesHelper';
import { getTagLabel } from '../../shared/helpers/tags/TagsHelper';
import { TAG_NAME } from '../../shared/utils/urls';
import { downloadBlobAsFile } from '../../shared/utils/utils';
import useExportData from '../../shared/hooks/projects/UseExportData';
import useExport from '../../shared/hooks/global/UseExport';
import DomainsPerModelSection from './components/domains-per-model/DomainsPerModelSection';
import WatchedPagesSection from './components/watched-pages/WatchedPagesSection';
import PageTrafficSection from './components/page-traffic/PageTrafficSection';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';
import { GET_PAGE_TRAFFIC } from '../../shared/api/hooks/monitor/data/sources/UseGetPageTraffic';
import { GET_PAGE_TRAFFIC_PROMPTS } from '../../shared/api/hooks/monitor/data/sources/UseGetPageTrafficPrompts';
import CompetitorSourcesSection from './components/competitor-sources/CompetitorSourcesSection';
import { isCompetitorDomain } from '../../shared/helpers/competitor/CompetitorHelper';
import {
  exportDomainsStats,
  getDomainsStats,
} from '../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import usePermission from '../../shared/hooks/global/UsePermission';

const SourcesPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const location = useLocation();

  const { projectId } = useParams();
  const { selectedChatType, selectedChatTypes, monitorPeriod, dateRange } = useFilters();
  const { startDate, endDate } = useProjectDates();

  const [chartDomain, setChartDomain] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const [sourcesPage, setSourcesPage] = useState(1);
  const [sourcesPageSize, setSourcesPageSize] = useState(10);
  const [localSelectedDomains, setLocalSelectedDomains] = useState<string[]>([]);
  const [domainsWithSourcesPage, setDomainsWithSourcesPage] = useState(1);
  const [domainsWithSourcesPageSize, setDomainsWithSourcesPageSize] = useState(10);

  const [sourcesKeyword, setSourcesKeyword] = useState('');
  const [domainsWithSourcesKeyword, setDomainsWithSourcesKeyword] = useState('');

  const [comparisonRange, setComparisonRange] = useState<(Date | null)[]>([null, null]);
  const isComparisonRangeManuallySet = useRef<boolean>(false);

  const navigate = useNavigate();

  const projectIdNumber = Number(projectId);
  const projectDomain = useProjectDomain(projectIdNumber);
  const { accountResponse, projectResponse, setProjectResponse } = useAccount();
  const currentProject = useMemo(
    () => projectResponse?.find((p) => p.id === projectIdNumber),
    [projectResponse, projectIdNumber]
  );

  // Hostnames Domain Share Comparison treats as "yours": project domain + website.
  const ownDomains = useMemo(() => {
    const domains: string[] = [];
    if (projectDomain) domains.push(projectDomain.toLowerCase());
    const websiteHost = currentProject?.website ? extractHostname(currentProject.website).toLowerCase() : '';
    if (websiteHost && !domains.includes(websiteHost)) domains.push(websiteHost);
    return domains;
  }, [projectDomain, currentProject?.website]);
  const queryClient = useQueryClient();
  const { canManage } = useCanManage();
  const projectsAccess = usePermission('PROJECTS');
  const { canExport } = useExport();
  const { exportSources } = useExportData(projectIdNumber);

  useProjectNavigate(projectIdNumber);

  const [searchParams] = useSearchParams();
  const { promptGroupId } = usePromptGroupId();
  const { monitorPrompts } = useGetMonitorsForProject(projectIdNumber);

  const filteredMonitorPromptIds = useMemo(() => {
    if (!monitorPrompts || promptGroupId === ALL_PROMPT_GROUPS_ID) return undefined;
    return monitorPrompts
      .filter((p) => p.promptGroupId === promptGroupId)
      .map((p) => p.id)
      .filter((id): id is number => id != null);
  }, [monitorPrompts, promptGroupId]);

  const hasExplicitModelFilter = searchParams.get('models') !== null;
  const filterMonitorTypes = useMemo(() => {
    if (!hasExplicitModelFilter || !selectedChatTypes || selectedChatTypes.length === 0) return undefined;
    return expandSelectedChatTypesToMonitorTypes(selectedChatTypes);
  }, [hasExplicitModelFilter, selectedChatTypes]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tableRef.current && !tableRef.current.contains(event.target as Node)) {
        setChartDomain(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filtersInitializing = useFiltersInitializing();

  // Calculate previous period dates based on comparison range
  const previousPeriodDates = useMemo(() => {
    if (!comparisonRange || !comparisonRange[0] || !comparisonRange[1]) {
      return null;
    }
    return {
      previousStartDate: comparisonRange[0],
      previousEndDate: comparisonRange[1],
    };
  }, [comparisonRange]);

  // Initialize comparison range based on main range only if it hasn't been manually set
  useEffect(() => {
    if (startDate && endDate && !isComparisonRangeManuallySet.current) {
      const periodLengthInDays = differenceInDays(endDate, startDate);
      const previousEndDate = subDays(startDate, 1);
      const previousStartDate = subDays(previousEndDate, periodLengthInDays);
      setComparisonRange([previousStartDate, previousEndDate]);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    setLocalSelectedDomains([]);
  }, [promptGroupId]);

  const domainsStatsPageSize = useMemo(() => {
    return localSelectedDomains.length > 0 ? Math.max(localSelectedDomains.length, 5) : 5;
  }, [localSelectedDomains]);

  const selectedDomainsForQuery = useMemo(() => {
    return localSelectedDomains.length > 0 ? localSelectedDomains : undefined;
  }, [localSelectedDomains]);

  const {
    domainsStats,
    isLoading: isDomainsStatsLoading,
    refetch: refetchDomainsStats,
  } = useGetDomainsStats(
    projectIdNumber,
    selectedChatType,
    startDate,
    endDate,
    1,
    domainsStatsPageSize,
    selectedDomainsForQuery,
    undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const {
    domainsStats: previousDomainsStats,
    isLoading: isPreviousDomainsStatsLoading,
    refetch: refetchPreviousDomainsStats,
  } = useGetDomainsStats(
    projectIdNumber,
    selectedChatType,
    previousPeriodDates?.previousStartDate || null,
    previousPeriodDates?.previousEndDate || null,
    1,
    domainsStatsPageSize,
    selectedDomainsForQuery,
    undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const { data: allDomainsData, isLoading: isAllDomainsLoading } = useQuery({
    queryKey: [
      'all-domains-filter',
      projectIdNumber,
      selectedChatType,
      startDate?.getTime(),
      endDate?.getTime(),
      filteredMonitorPromptIds,
      filterMonitorTypes,
      ownDomains,
    ],
    // Top 100 domains by citations — `selectedFirst` puts the own domains in front of that cut, otherwise a
    // modestly cited own site (e.g. rank 222 of 1443) is missing from the response and shows as 0%.
    queryFn: () =>
      getDomainsStats(
        projectIdNumber,
        true,
        selectedChatType!,
        startDate,
        endDate,
        1,
        100,
        ownDomains.length ? ownDomains : undefined,
        undefined,
        filteredMonitorPromptIds,
        filterMonitorTypes
      ),
    enabled: !!projectIdNumber && !!startDate && !!endDate && !!selectedChatType,
    staleTime: 1000 * 60 * 5,
  });

  const {
    domainsDailyStats,
    isLoading: isDomainsDailyStatsLoading,
    refetch: refetchDomainsDailyStats,
  } = useGetDomainsDailyStats(
    projectIdNumber,
    selectedChatType,
    monitorPeriod,
    startDate,
    endDate,
    selectedDomainsForQuery,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const {
    domainsWithSourcesStats,
    isLoading: isDomainsWithSourcesStatsLoading,
    refetch: refetchDomainsWithSourcesStats,
  } = useGetDomainsWithSourcesStats(
    projectIdNumber,
    selectedChatType,
    startDate,
    endDate,
    domainsWithSourcesPage,
    domainsWithSourcesPageSize,
    undefined,
    domainsWithSourcesKeyword || undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const {
    domainsWithSourcesStats: previousDomainsWithSourcesStats,
    isLoading: isPreviousDomainsWithSourcesStatsLoading,
    refetch: refetchPreviousDomainsWithSourcesStats,
  } = useGetDomainsWithSourcesStats(
    projectIdNumber,
    selectedChatType,
    previousPeriodDates?.previousStartDate || null,
    previousPeriodDates?.previousEndDate || null,
    domainsWithSourcesPage,
    domainsWithSourcesPageSize,
    undefined,
    domainsWithSourcesKeyword || undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const domainsStatsWithShare = useDomainsStatsShare(domainsStats);
  const previousDomainsStatsWithShare = useDomainsStatsShare(previousDomainsStats);

  const domainsStatsComparison = useDomainsStatsComparison(
    domainsStatsWithShare,
    previousDomainsStatsWithShare
  );

  const citationTrendsMatrix = useDomainsDailyStatsMatrix(
    domainsDailyStats,
    domainsStatsWithShare,
    projectDomain,
    selectedDomainsForQuery
  );


  // SOURCES_3_0 was released to everyone; the source-categories / 3.0 UI is always on now.
  const hasSourceCategories = true;
  const { categoriesStats } = useGetCategoriesStats(
    projectIdNumber,
    selectedChatType,
    startDate,
    endDate,
    hasSourceCategories,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const { shouldShow: shouldShowCategoriesChart } =
    useCitationTypesShareData(categoriesStats, hasSourceCategories);

  const domainsWithSourcesStatsWithShare = useDomainsWithSourcesStatsShare(domainsWithSourcesStats);
  const previousDomainsWithSourcesStatsWithShare = useDomainsWithSourcesStatsShare(
    previousDomainsWithSourcesStats
  );

  const domainsWithSourcesStatsComparison = useDomainsWithSourcesStatsComparison(
    domainsWithSourcesStatsWithShare,
    previousDomainsWithSourcesStatsWithShare
  );

  const {
    sourcesStats,
    isLoading: isSourcesStatsLoading,
    refetch: refetchSourcesStats,
  } = useGetSourceStats(
    projectIdNumber,
    selectedChatType,
    startDate,
    endDate,
    sourcesPage,
    sourcesPageSize,
    undefined,
    sourcesKeyword || undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const {
    sourcesStats: previousSourcesStats,
    isLoading: isPreviousSourcesStatsLoading,
    refetch: refetchPreviousSourcesStats,
  } = useGetSourceStats(
    projectIdNumber,
    selectedChatType,
    previousPeriodDates?.previousStartDate || null,
    previousPeriodDates?.previousEndDate || null,
    sourcesPage,
    sourcesPageSize,
    undefined,
    sourcesKeyword || undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const sourcesStatsWithShare = useSourcesStatsShare(sourcesStats);
  const previousSourcesStatsWithShare = useSourcesStatsShare(previousSourcesStats);

  const sourcesStatsComparison = useSourceStatsComparison(
    sourcesStatsWithShare,
    previousSourcesStatsWithShare
  );

  const { commonKeywords } = useGetCommonKeywords(projectIdNumber);

  const { tags } = useGetTags(projectIdNumber);
  const brandNames = useMemo(() => withTagBrandNames(commonKeywords, tags), [commonKeywords, tags]);

  const firmName = useMemo(() => {
    if (!currentProject) return '';
    const rawFirmName = currentProject.firmName || '';
    if (!rawFirmName.startsWith(TAG_NAME)) {
      return rawFirmName;
    }
    const tagId = Number(rawFirmName.slice(TAG_NAME.length)) || null;
    return getTagLabel(tags, tagId, rawFirmName);
  }, [currentProject, tags]);

  const competition = useMemo(() => currentProject?.competition ?? [], [currentProject]);

  const competitorKeywords = useMemo(() => {
    if (!commonKeywords?.length || !firmName) return [];
    const firmNorm = firmName.toLowerCase().trim();
    return commonKeywords.filter((k) => k.normalizedKeyword.toLowerCase() !== firmNorm);
  }, [commonKeywords, firmName]);

  const competitorKeywordNames = useMemo(
    () => competitorKeywords.map((k) => k.normalizedKeyword),
    [competitorKeywords]
  );

  const domainClassification = currentProject?.domainClassification || null;

  const competitorCount = useMemo(() => {
    if (!allDomainsData?.domains?.length) return 0;
    return allDomainsData.domains
      .filter((d) => {
        const override = domainClassification?.[d.domain];
        if (override) return override === 'COMPETITOR';
        return isCompetitorDomain(d.domain, competitorKeywords, competition);
      })
      .reduce((sum, d) => sum + d.count, 0);
  }, [allDomainsData, competition, competitorKeywords, domainClassification]);

  const { chartData: categoriesChartData } = useCitationTypesShareData(
    categoriesStats, hasSourceCategories, hasSourceCategories ? competitorCount : 0
  );

  const handleUpdateDomainClassification = useCallback(
    (classification: Record<string, string>) => {
      if (!projectIdNumber) return;
      setProjectResponse((prev) =>
        prev
          ? prev.map((p) =>
              p.id === projectIdNumber ? { ...p, domainClassification: classification } : p
            )
          : prev
      );
      // Moving a domain into / out of "Your domains" changes the scope URL Overview is built
      // from — the backend derives it from domainClassification (resolveOwnDomains). Its query
      // key can't carry the classification, and the query never refetches on its own
      // (staleTime 5min, refetchOnMount false), so it has to be invalidated explicitly once the
      // write reaches the server. The prefix keys cover every mode / filter combination — and the
      // per-URL prompt lists, which are scoped by the same own domains and cached just as long.
      void updateDomainClassification(projectIdNumber, classification).then(() => {
        queryClient.invalidateQueries({ queryKey: [GET_PAGE_TRAFFIC, projectIdNumber] });
        queryClient.invalidateQueries({ queryKey: [GET_PAGE_TRAFFIC_PROMPTS, projectIdNumber] });
      });
    },
    [projectIdNumber, setProjectResponse, queryClient]
  );

  const periodFilterItems: FilterItem[] = useMemo(() => {
    if (!selectedChatType || !monitorPeriod) {
      return [];
    }
    const replaceLastTwoSegments = (url: string, period: string, chat: string): string => {
      const parts = url.split('/').filter(Boolean);
      const newParts = parts.slice(0, -2);
      return `/${newParts.join('/')}/${period}/${chat}`;
    };
    const chat = selectedChatType.toLowerCase();
    return [
      {
        url: replaceLastTwoSegments(location.pathname, 'daily', chat),
        label: t('dailyFilter'),
        isActive: monitorPeriod === 'DAILY',
      },
      {
        url: replaceLastTwoSegments(location.pathname, 'weekly', chat),
        label: t('weeklyFilter'),
        isActive: monitorPeriod === 'WEEKLY',
      },
      {
        url: replaceLastTwoSegments(location.pathname, 'monthly', chat),
        label: t('monthlyFilter'),
        isActive: monitorPeriod === 'MONTHLY',
      },
    ];
  }, [location.pathname, selectedChatType, monitorPeriod, t]);

  const handleComparisonRangeChange = (newRange: Date[]): void => {
    isComparisonRangeManuallySet.current = true;
    setComparisonRange(newRange);
  };

  const handleExportDomainsStats = async (): Promise<void> => {
    if (!canExport || !startDate || !endDate || !selectedChatType) {
      return;
    }

    const blob = await exportDomainsStats(projectIdNumber, selectedChatType, startDate, endDate);
    const fileName = `domains-${selectedChatType}-${format(startDate, 'yyyy-MM-dd')}-${format(
      endDate,
      'yyyy-MM-dd'
    )}.csv`;
    downloadBlobAsFile(blob, fileName);
  };

  if (!accountResponse || !accountResponse.addons.includes('SOURCES_STATS') || !projectId) {
    navigate(PROJECTS);
    return <></>;
  }

  // Filtry strony: na desktopie w nagłówku (`Title`), na telefonie pod górnym paskiem.
  const filters = (
    <FilterPanel baseUrl={location.pathname} showPeriodTabs={false}>
      <PromptGroupDropdown projectId={projectIdNumber} />
      <ComparisonDateRangePicker
        value={comparisonRange}
        onChange={handleComparisonRangeChange}
        referenceStartDate={startDate}
        referenceEndDate={endDate}
        minDate={dateRange?.oldestDate}
        maxDate={dateRange?.newestDate}
      />
    </FilterPanel>
  );

  return (
    <>
      <div className="sources-page page-content">
        <div className="page-box">
          {/* Telefon: tytuł w górnym pasku, filtry na górze strony pod nim (jak na dashboardzie). */}
          <MobilePageHeader title={<span>{t('sources')}</span>} />
          {isMobile ? (
            <div className="mobile-page-filters">{filters}</div>
          ) : (
            <div className="page-box__filters" data-observe-sticky>
              <Title
                isBaseCategory={false}
                title={t('sources')}
                children={<div className="filters">{filters}</div>}
                showStatusDot={true}
              />
            </div>
          )}
          <div className="page-box__stats">
            <div className="stats stats--full-width">
              <DomainsDailyStatsChart
                matrix={citationTrendsMatrix}
                selectedDomain={chartDomain}
                domainsStatsWithShare={domainsStatsWithShare}
                isLoading={isDomainsDailyStatsLoading || filtersInitializing}
                periodFilterItems={periodFilterItems}
                period={monitorPeriod}
              />
            </div>
            <div className="stats stats--full-width">
              <DomainsPerModelSection
                projectId={projectIdNumber}
                startDate={startDate}
                endDate={endDate}
                features={accountResponse?.features || []}
                selectedChatType={selectedChatType}
                monitorPromptIds={filteredMonitorPromptIds}
                selectedChatTypes={hasExplicitModelFilter ? selectedChatTypes : undefined}
                filterMonitorTypes={filterMonitorTypes}
              />
            </div>
            {shouldShowCategoriesChart && (
              <div className="stats stats--full-width">
                <CitationTypesShareBarChart chartData={categoriesChartData} />
              </div>
            )}


            <DomainsWithSourcesStatsTable
              domains={domainsWithSourcesStatsComparison?.domains || []}
              pagination={domainsWithSourcesStatsComparison?.pagination}
              keyword={domainsWithSourcesKeyword}
              commonKeywords={brandNames as unknown as KeywordResponse[]}
              tags={tags}
              startDate={startDate}
              endDate={endDate}
              previousStartDate={previousPeriodDates?.previousStartDate || null}
              previousEndDate={previousPeriodDates?.previousEndDate || null}
              onKeywordChange={(keyword) => {
                setDomainsWithSourcesKeyword(keyword);
                setDomainsWithSourcesPage(1); // Reset to first page when keyword changes
              }}
              onPageChange={(page, pageSize) => {
                setDomainsWithSourcesPage(page);
                setDomainsWithSourcesPageSize(pageSize);
              }}
              isLoading={
                isDomainsWithSourcesStatsLoading ||
                isPreviousDomainsWithSourcesStatsLoading ||
                filtersInitializing
              }
              hasAdvancedFeatures={accountResponse?.addons.includes('SOURCES_STATS_ADVANCED') ?? false}
              firmName={firmName}
              canExport={canExport}
              onExport={handleExportDomainsStats}
              hasDomainDetailPage={hasSourceCategories}
            />
            <SourcesStatsTable
              sources={sourcesStatsComparison?.sources || []}
              pagination={sourcesStatsComparison?.pagination}
              commonKeywords={brandNames as unknown as KeywordResponse[]}
              tags={tags}
              isLoading={isSourcesStatsLoading || isPreviousSourcesStatsLoading || filtersInitializing}
              keyword={sourcesKeyword}
              startDate={startDate}
              endDate={endDate}
              previousStartDate={previousPeriodDates?.previousStartDate || null}
              previousEndDate={previousPeriodDates?.previousEndDate || null}
              onKeywordChange={(keyword) => {
                setSourcesKeyword(keyword);
                setSourcesPage(1);
              }}
              onPageChange={(page, pageSize) => {
                setSourcesPage(page);
                setSourcesPageSize(pageSize);
              }}
              hasAdvancedFeatures={accountResponse?.addons.includes('SOURCES_STATS_ADVANCED') ?? false}
              firmName={firmName}
              onExport={exportSources}
              hasSourceDetailPage={hasSourceCategories}
            />
            <div className="stats stats--full-width">
              <CompetitorSourcesSection
                allDomainsData={allDomainsData}
                ownDomains={ownDomains}
                competitorKeywords={competitorKeywords}
                competition={competition}
                isLoading={isAllDomainsLoading || filtersInitializing}
                domainClassification={currentProject?.domainClassification || null}
                onUpdateDomainClassification={handleUpdateDomainClassification}
                // `canManage` to limit planu, nie uprawnienie — przeciąganie domeny zapisuje
                // klasyfikację w opcjach projektu, więc bez prawa zapisu (gość) musi być wyłączone.
                canEdit={canManage && projectsAccess.write}
                showOverrideMarkers={accountResponse?.isSuperAdmin ?? false}
              />
            </div>
            <div className="stats stats--full-width">
              <PageTrafficSection
                projectId={projectIdNumber}
                chatType={selectedChatType}
                from={startDate}
                to={endDate}
                monitorPromptIds={filteredMonitorPromptIds}
                filterMonitorTypes={filterMonitorTypes}
              />
            </div>
            <div className="stats stats--full-width">
              <WatchedPagesSection
                projectId={projectIdNumber}
                firmName={firmName}
                chatType={selectedChatType || 'ALL'}
                tags={tags}
                hasAdvancedFeatures={accountResponse?.addons.includes('SOURCES_STATS_ADVANCED') ?? false}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SourcesPage;
